/**
 * @fileoverview Seção "Triagem" do painel da OS: as triagens desta OS, a espera
 * ou a falha do início automático e o botão "Iniciar triagem" (diálogo com a
 * triagem e o telefone). Quem pode ver a tela de OS (admin) inicia por aqui;
 * com a regra nova ligada, quem tem work_orders.start_triage.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ModernSelect from "../../components/ModernSelect";
import usePermission from "../../hooks/usePermission";
import useStyles from "./styles";
import { formatLocalDateTime, formatPhone } from "./format";
import { triageChipClass, triageStateLabel, triageStateOf } from "./triageState";

const t = (key, params) => i18n.t(`workOrders.triage.${key}`, params);
const ATIVAS = ["PENDING", "IN_PROGRESS", "WAITING_CUSTOMER"];

const WorkOrderTriage = ({ workOrderId }) => {
  const classes = useStyles();
  const history = useHistory();
  const canStart = usePermission().can("work_orders.start_triage", true);
  const [info, setInfo] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [definitionId, setDefinitionId] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(async () => {
    const { data } = await api.get(`/work-orders/${workOrderId}/triage`);
    setInfo(data);
    return data;
  }, [workOrderId]);

  useEffect(() => {
    setInfo(null);
    carregar().catch(toastError);
  }, [carregar]);

  const disponiveis = (info?.definitions || []).filter((d) => d.available);
  const escolhida = (info?.definitions || []).find((d) => String(d.id) === String(definitionId)) || null;

  const abrirDialogo = () => {
    setDefinitionId(disponiveis.length === 1 ? disponiveis[0].id : "");
    setPhone(info?.phones?.[0]?.number || "");
    setAberto(true);
  };

  const iniciar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/work-orders/${workOrderId}/triage`, { definitionId, phone });
      toast.success(data.outcome === "waiting_human" ? t("startedHuman") : t("started"));
      setAberto(false);
      await carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!info) {
    return (
      <div className={classes.section}>
        <div className={classes.sectionTitle}>{t("title")}</div>
        <CircularProgress size={18} />
      </div>
    );
  }

  const ativa = info.sessions.some((s) => ATIVAS.includes(s.status));
  const offersStart = canStart && info.enabled && info.definitions.length > 0 && !ativa;

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>{t("title")}</div>

      {!info.enabled && <div className={classes.triageNote}>{t("disabled")}</div>}
      {info.enabled && !ativa && info.workOrderStatus === "received" && info.nextAttemptAt && (
        <div className={classes.triageNote}>
          {t("waiting", { when: formatLocalDateTime(info.nextAttemptAt) })}
          {info.lastError ? ` ${info.lastError}` : ""}
        </div>
      )}
      {info.enabled && !ativa && info.workOrderStatus === "failed" && (
        <div className={classes.triageNote}>{`${t("failed")} ${info.lastError || ""}`.trim()}</div>
      )}

      {info.sessions.length === 0 ? (
        <div className={classes.dialogHint}>{t("none")}</div>
      ) : (
        info.sessions.map((s) => (
          <div key={s.id} className={classes.triageRow}>
            <div className={classes.triageMain}>
              <span className={classes.triageName}>
                {s.definitionName || t("unnamed")}
                <span className={triageChipClass(classes, triageStateOf(s))}>{triageStateLabel(triageStateOf(s))}</span>
              </span>
              <span className={classes.tdMuted}>{formatLocalDateTime(s.createdAt)}</span>
            </div>
            {s.ticketUuid && (
              <button
                type="button"
                className={`${classes.linkBtn} ${classes.triageOpen}`}
                onClick={() => history.push(`/tickets/${s.ticketUuid}`)}
              >
                {t("openConversation")}
              </button>
            )}
          </div>
        ))
      )}

      {info.enabled && info.definitions.length === 0 && <div className={classes.dialogHint}>{t("noDefinitions")}</div>}

      {offersStart && (
        <div className={classes.triageActions}>
          <button
            type="button"
            className={classes.primaryBtn}
            disabled={!disponiveis.length || !info.phones.length}
            onClick={abrirDialogo}
          >
            {t("start")}
          </button>
        </div>
      )}
      {offersStart && !info.phones.length && (
        <div className={classes.dialogHint} style={{ marginTop: 6 }}>{t("noPhones")}</div>
      )}
      {offersStart && info.phones.length > 0 && !disponiveis.length && (
        <div className={classes.dialogHint} style={{ marginTop: 6 }}>
          {info.definitions.map((d) => (
            <div key={d.id}>{`${d.name}: ${d.reason}`}</div>
          ))}
        </div>
      )}

      <Dialog open={aberto} onClose={() => !busy && setAberto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("dialogTitle")}</DialogTitle>
        <DialogContent>
          <div className={classes.dialogBody}>
            <ModernSelect
              size="small"
              fullWidth
              label={t("definition")}
              InputLabelProps={{ shrink: true }}
              displayEmpty
              value={definitionId}
              onChange={(ev) => setDefinitionId(ev.target.value)}
            >
              <MenuItem value="">
                <em>{t("chooseDefinition")}</em>
              </MenuItem>
              {info.definitions.map((d) => (
                <MenuItem key={d.id} value={d.id} disabled={!d.available}>
                  {d.available ? d.name : `${d.name} · ${d.reason}`}
                </MenuItem>
              ))}
            </ModernSelect>

            <div>
              <div className={classes.dialogLabel}>{t("phone")}</div>
              <RadioGroup className={classes.radioGroup} value={phone} onChange={(ev) => setPhone(ev.target.value)}>
                {info.phones.map((p) => (
                  <FormControlLabel
                    key={p.number}
                    value={p.number}
                    control={<Radio size="small" color="primary" />}
                    label={`${formatPhone(p.number.slice(2))} · ${p.mobile ? t("mobile") : t("landline")}`}
                  />
                ))}
              </RadioGroup>
            </div>

            {escolhida?.connection && (
              <div className={classes.dialogHint}>
                {`${t("connectionNote")} ${escolhida.connection.name}`}
              </div>
            )}
            <div className={classes.dialogHint}>{t("dialogHint")}</div>
          </div>
        </DialogContent>
        <DialogActions>
          <button type="button" className={classes.linkBtn} onClick={() => setAberto(false)} disabled={busy}>
            {t("cancel")}
          </button>
          <button type="button" className={classes.primaryBtn} onClick={iniciar} disabled={busy || !definitionId || !phone}>
            {busy ? <CircularProgress size={14} color="inherit" /> : t("confirm")}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WorkOrderTriage;
